import { STORE_SCREEN } from 'utils/storeScreen';
import { leaveSignedOut } from 'utils/signedOut';
import { RestLink } from 'apollo-link-rest/restLink';
import { AxiosError } from 'axios';
import { AdDateRange } from 'types/adTypes';
import { AppEvents } from 'types/appEvents';
import { HttpStatusCodes } from 'types/requestTypes';
import { Routes } from 'types/routeTypes';
import { getLocalAppSession } from './storageHelpers';
import { isQuietPreviewFailure } from './templatePreviewQueue';

interface ApiError {
  message: string;
  status: number;
}

export const apiError = (error: AxiosError<ApiError>) => {
  return new Error(
    `${error?.response?.data?.message ?? 'Network error'} with status code: ${
      error?.response
        ? HttpStatusCodes[error.response.status]
        : HttpStatusCodes.NETWORKERROR
    }`
  );
};

export const UPLOAD_ERROR_DESCRIPTION =
  'We were unable to upload your file. Please contact us if this problem persists!';

/**
 * SYM-6610. An upload that dies before the server answers (offline, timeout, abort) has no
 * response, so read it optionally. Only a 422 carries a message meant for the user.
 */
export const uploadErrorDescription = (error: unknown): string => {
  const response = (error as AxiosError<{ message?: string }> | undefined)
    ?.response;
  if (response?.status !== 422) return UPLOAD_ERROR_DESCRIPTION;
  return response.data?.message ?? UPLOAD_ERROR_DESCRIPTION;
};

export const dateFilterToNumeric = (date: AdDateRange) => {
  switch (date) {
    // TODO: Change this value when all time gets added to the API
    case 'Last year':
      return 365;
    case 'Last quarter':
      return 90;
    case 'Last 14 days':
      return 14;
    case 'Last month':
      return 30;
    case 'Last week':
      return 7;
  }
};

export const getMetricDaysFilter = () => {
  const session = getLocalAppSession();
  return session.activeDateFilter
    ? dateFilterToNumeric(session.activeDateFilter)
    : 90;
};

/**
 *
 * @param uri
 * @param options
 * @returns
 */
/**
 * Every store this page sends says which screen it comes from (D10): the
 * server then never takes this page's own stores for "someone else's".
 * Only bodies that are one JSON thing with named fields are touched.
 */
const namingTheScreen = (options: RequestInit): RequestInit => {
  const method = (options?.method ?? 'GET').toUpperCase();
  if (method === 'GET' || typeof options?.body !== 'string') return options;
  try {
    const body = JSON.parse(options.body) as unknown;
    if (body === null || typeof body !== 'object' || Array.isArray(body)) {
      return options;
    }
    return {
      ...options,
      body: JSON.stringify({ ...body, store_screen: STORE_SCREEN }),
    };
  } catch (_e) {
    return options;
  }
};

export const customFetch: RestLink.CustomFetch = async (
  uri: RequestInfo,
  options: RequestInit
) => {
  let res: Response;

  try {
    res = await fetch(uri, namingTheScreen(options));

    if (res.status === HttpStatusCodes.UNAUTHORIZED) {
      // Redirect unauthorized users if not already on the login page
      if (window.location.pathname !== Routes.ROOT) leaveSignedOut();
    } else if (res.status === HttpStatusCodes.TWO_FACTOR_PENDING) {
      if (window.location.pathname !== Routes.TWO_FACTOR)
        window.location.replace(Routes.TWO_FACTOR);
    } else {
      // SYM-6213: a "busy" template preview is retried quietly by the editor, so it must not
      // raise a toast. Everything else still does.
      if (!res.ok && !isQuietPreviewFailure(uri, res.status)) {
        dispatchEvent(
          new CustomEvent(AppEvents.NETWORK_ERROR, {
            detail: res,
          })
        );
      }
    }
  } catch (err) {
    // SYM-6211: the browser never reached the server (offline, cancelled, blocked). Reject with a
    // real Error: the query library reads only `.message`, and shows "Error message not found."
    // without one. No toast here: the toast reads a server response, and there is none.
    return Promise.reject(err instanceof Error ? err : new Error(String(err)));
  }

  // Return the network response
  return Promise.resolve(res);
};
